/**
 * Main Layout Component
 */
import { Box, useTheme } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useGetProfileQuery } from '../../store/api/usersApi';
import { updateUser } from '../../store/slices/authSlice';
import Sidebar from './Sidebar';
import Header from './Header';

const MainLayout = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { data: profileData } = useGetProfileQuery(undefined, {
    skip: !isAuthenticated,
  });

  // Keep Redux user in sync with GET /users/profile/ (permissions, employee_profile, flags).
  // Previously only name/avatar updated, so sidebar RBAC stayed stale vs localStorage after role changes.
  useEffect(() => {
    if (profileData?.data) {
      const data = profileData.data;
      const ep = data.employee_profile;
      dispatch(
        updateUser({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          full_name: data.full_name || `${data.first_name || ''} ${data.last_name || ''}`.trim(),
          profile_picture: data.profile_picture || null,
          is_staff: data.is_staff,
          is_superuser: data.is_superuser ?? false,
          employee_profile: ep
            ? {
                id: ep.id,
                employee_id: ep.employee_id,
                role: ep.role,
                department: ep.department || null,
                position: ep.position || null,
                status: ep.status,
                can_access_dashboard: ep.can_access_dashboard,
                use_only_custom_permissions: ep.use_only_custom_permissions,
                permissions: ep.permissions,
                custom_permissions: ep.custom_permissions,
                role_permissions: ep.role_permissions,
                permission_breakdown: ep.permission_breakdown,
              }
            : null,
        })
      );
    }
  }, [profileData, dispatch]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Header />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            minWidth: 0,
            overflowX: 'auto',
            p: 0,
            mt: { xs: '56px', sm: '64px' },
            backgroundColor: theme.palette.background.default,
            minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
            boxSizing: 'border-box',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;